import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, ListItem, OrderedList, Tooltip, useMultiStyleConfig } from '@chakra-ui/react';
import { CheckIcon, WarningIcon } from '@chakra-ui/icons';

import { AdStep, AdValidationStatus } from '@/store/ad/types';
import { StepsComponentName } from '@/theme';
import { useMatomoTagManager } from '@/hooks';

export type NavigationBarProps = {
  currentStepIndex: number;
  steps: AdStep[];
  gotoStep: (step: string) => void;
  validationStatus: AdValidationStatus;
};

type StepStatus = 'inactive' | 'active' | 'completed' | 'invalid';

export const NavigationBar: FC<NavigationBarProps> = memo(function NavigationBar({
  currentStepIndex,
  steps,
  gotoStep,
  validationStatus,
}: NavigationBarProps) {
  const { t } = useTranslation();
  const styles = useMultiStyleConfig(StepsComponentName);

  const { trackMatomoEvent } = useMatomoTagManager();

  const visibleSteps = useMemo(() => steps.filter((s) => !s.isHidden), [steps]);

  const getStatus = (step: string): StepStatus => {
    const stepIndex = steps.findIndex((s) => s.key === step);
    if (stepIndex < currentStepIndex) return validationStatus[step]?.isValid ? 'completed' : 'invalid';
    if (stepIndex === currentStepIndex) return 'active';
    return 'inactive';
  };

  const handleStepClick = (step: string) => {
    trackMatomoEvent({
      trackingCategory: 'Interaction_PI-Insertion',
      trackingAction: 'Jump_To_InsertionStep',
      trackingName: `Jump_To_InsertionStep_${step}`,
    });
    gotoStep(step);
  };

  if (!visibleSteps.length) {
    return null;
  }

  return (
    <Flex justify="center">
      <Box sx={styles.container}>
        <OrderedList sx={styles.steps}>
          {visibleSteps.map((step, i) => {
            const status = getStatus(step.key);
            return (
              <Tooltip
                key={i}
                label={
                  <>
                    {status === 'invalid' && <WarningIcon mr={2} />}
                    {/* @ts-expect-error dynamic translation */}
                    {step.titleResourceKey && t(step.titleResourceKey)}
                  </>
                }
                isDisabled={!step.titleResourceKey}
                hasArrow
              >
                <ListItem sx={styles[`step.${status}`]} onClick={() => handleStepClick(step.key)}>
                  <Box className="step-circle">{status === 'completed' ? <CheckIcon /> : i + 1}</Box>
                  {step.titleResourceKey && (
                    <Box className="step-label">
                      {/* @ts-expect-error dynamic translation */}
                      {t(step.titleResourceKey)}
                    </Box>
                  )}
                </ListItem>
              </Tooltip>
            );
          })}
        </OrderedList>
      </Box>
    </Flex>
  );
});
